import { useAnalytics } from "@/services/posthog/hooks";
import * as Sentry from "@sentry/react";
import { Session, User } from "@supabase/supabase-js";
import { createContext, useEffect, useState } from "react";
import { supabase } from "../../createClient";
import { clearLocalData } from "../actions/clearLocalData";

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
  isError: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isAuthenticated: false,
  isInitialized: false,
  isError: false,
});

export const AuthProvider = ({ children }: React.PropsWithChildren) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isInitialized, setInitialized] = useState(false);
  const [isError, setError] = useState(false);
  const analytics = useAnalytics();

  useEffect(() => {
    try {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setInitialized(true);
        analytics.identify(session?.user.id);
        Sentry.setTag("user_id", session?.user.id);
      });
    } catch (error) {
      setError(true);
    }

    const res = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      analytics.identify(session?.user.id);
      Sentry.setTag("user_id", session?.user.id);

      if (!session) {
        clearLocalData();
      }
    });

    const subscription = res.data.subscription;

    return () => subscription.unsubscribe();
  }, [analytics]);

  return (
    <AuthContext.Provider
      value={{ user: session?.user || null, isAuthenticated: !!session, isError, session, isInitialized }}
    >
      {children}
    </AuthContext.Provider>
  );
};
